/**
 * Page-space geometry over raw sketch records, shared by apply, projection,
 * and grid serialization. Everything here is Y-down page units; callers flip
 * through units.ts at the presentation boundary.
 */
import { invariant } from "ts-invariant";

import type { CalloutBindingProps, CalloutProps } from "./shapes/callout";
import type {
  DimAnchor,
  DimBindingProps,
  DimProps,
  DimTerminal,
} from "./shapes/dim";
import type { GridlineProps } from "./shapes/gridline";

export interface ShapeRec {
  id: string;
  typeName: "shape";
  type: string;
  x: number;
  y: number;
  rotation: number;
  index: string;
  parentId: string;
  isLocked: boolean;
  opacity: number;
  meta: Record<string, unknown>;
  props: Record<string, any>;
}

export interface PageRec {
  id: string;
  typeName: "page";
  name: string;
  index: string;
  meta: Record<string, unknown>;
}

interface BindingRecOf<Type extends string, Props> {
  id: string;
  typeName: "binding";
  type: Type;
  fromId: string;
  toId: string;
  props: Props;
  meta: Record<string, unknown>;
}

export type DimBindingRec = BindingRecOf<"dim", DimBindingProps>;
export type CalloutBindingRec = BindingRecOf<"callout", CalloutBindingProps>;
export type BindingRec = DimBindingRec | CalloutBindingRec;

export type SketchRecords = Record<
  string,
  { typeName: string } & Record<string, any>
>;

export interface PagePoint {
  x: number;
  y: number;
}

export interface PageBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function pagesOf(records: SketchRecords): PageRec[] {
  return Object.values(records)
    .filter((r): r is PageRec => r.typeName === "page")
    .sort((a, b) => (a.index < b.index ? -1 : 1));
}

export function shapesOf(records: SketchRecords, pageId: string): ShapeRec[] {
  return Object.values(records)
    .filter(
      (r): r is ShapeRec => r.typeName === "shape" && r.parentId === pageId,
    )
    .sort((a, b) => (a.index < b.index ? -1 : 1));
}

export function linePointsPage(shape: ShapeRec): [PagePoint, PagePoint] {
  const points = Object.values(
    shape.props.points as Record<
      string,
      { index: string; x: number; y: number }
    >,
  ).sort((a, b) => (a.index < b.index ? -1 : 1));
  const [a, b] = points;
  invariant(a && b, `line ${shape.id} needs two points`);
  return [
    { x: shape.x + a.x, y: shape.y + a.y },
    { x: shape.x + b.x, y: shape.y + b.y },
  ];
}

export function gridlinePosition(shape: ShapeRec): number {
  const props = shape.props as GridlineProps;
  return props.axis === "x" ? shape.x : shape.y;
}

export function boundsPage(shape: ShapeRec): PageBox {
  if (shape.type === "line") {
    const [a, b] = linePointsPage(shape);
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
  }
  if (shape.type === "gridline") {
    const pos = gridlinePosition(shape);
    const props = shape.props as GridlineProps;
    return props.axis === "x"
      ? { x: pos, y: 0, w: 0, h: 0 }
      : { x: 0, y: pos, w: 0, h: 0 };
  }
  const w = (shape.props.w as number | undefined) ?? 0;
  const h = (shape.props.h as number | undefined) ?? 0;
  return { x: shape.x, y: shape.y, w, h };
}

export function dimBindings(
  records: SketchRecords,
  dimId: string,
): Partial<Record<DimTerminal, DimBindingRec>> {
  const out: Partial<Record<DimTerminal, DimBindingRec>> = {};
  for (const r of Object.values(records)) {
    if (r.typeName !== "binding" || r.type !== "dim") continue;
    const binding = r as DimBindingRec;
    if (binding.fromId !== dimId) continue;
    out[binding.props.terminal] = binding;
  }
  return out;
}

/** A callout has one leader, so at most one binding. */
export function calloutBinding(
  records: SketchRecords,
  calloutId: string,
): CalloutBindingRec | undefined {
  for (const r of Object.values(records)) {
    if (r.typeName !== "binding" || r.type !== "callout") continue;
    const binding = r as CalloutBindingRec;
    if (binding.fromId === calloutId) return binding;
  }
  return undefined;
}

/** Where a bound endpoint lands on its target, in page space. */
export function anchorPointPage(
  target: ShapeRec,
  anchor: PagePoint,
): PagePoint {
  const b = boundsPage(target);
  return { x: b.x + anchor.x * b.w, y: b.y + anchor.y * b.h };
}

export const otherTerminal = (terminal: DimTerminal): DimTerminal =>
  terminal === "start" ? "end" : "start";

/**
 * Where a binding's anchor lands in page space, shared by every annotation
 * that pins an end to a shape (dims and callout leaders), and the headless
 * twin of the app's `boundAnchorPage`. A vertex binding resolves to that
 * exact point; everything else resolves on the target's bounds.
 */
export function boundAnchorPage(target: ShapeRec, ref: DimAnchor): PagePoint {
  return (
    boundVertexPage(target, ref.vertex) ?? anchorPointPage(target, ref.anchor)
  );
}

/** The point an endpoint is pinned to, before any edge is resolved: a bound
 *  anchor or vertex, else the dim's own stored point. */
function dimRawEndpointPage(
  records: SketchRecords,
  dim: ShapeRec,
  terminal: DimTerminal,
): PagePoint {
  const binding = dimBindings(records, dim.id)[terminal];
  if (!binding) return (dim.props as DimProps)[terminal];
  const target = records[binding.toId] as ShapeRec | undefined;
  invariant(target, `dim ${dim.id} bound to missing shape ${binding.toId}`);
  return boundAnchorPage(target, binding.props);
}

/** The foot of the perpendicular from `p` onto the line through `a` and `b`,
 *  which is what an endpoint measuring to an edge resolves to. */
export function footOnLine(
  p: PagePoint,
  a: PagePoint,
  b: PagePoint,
): PagePoint {
  const span = { x: b.x - a.x, y: b.y - a.y };
  const lengthSquared = span.x * span.x + span.y * span.y;
  if (lengthSquared === 0) return a;
  const t = ((p.x - a.x) * span.x + (p.y - a.y) * span.y) / lengthSquared;
  return { x: a.x + span.x * t, y: a.y + span.y * t };
}

export function dimEndpointPage(
  records: SketchRecords,
  dim: ShapeRec,
  terminal: DimTerminal,
): PagePoint {
  const binding = dimBindings(records, dim.id)[terminal];
  const edge = binding?.props.edge;
  if (!binding || !edge) return dimRawEndpointPage(records, dim, terminal);
  const target = records[binding.toId] as ShapeRec | undefined;
  invariant(target, `dim ${dim.id} bound to missing shape ${binding.toId}`);
  // Resolved against the other end's own pin, never against its resolved
  // point: two edges facing each other would otherwise chase each other.
  return footOnLine(
    dimRawEndpointPage(records, dim, otherTerminal(terminal)),
    boundAnchorPage(target, binding.props),
    boundAnchorPage(target, edge),
  );
}

/** Where a callout's leader lands: its bound target, or the free point. */
export function calloutTargetPage(
  records: SketchRecords,
  callout: ShapeRec,
): PagePoint {
  const binding = calloutBinding(records, callout.id);
  if (binding) {
    const target = records[binding.toId] as ShapeRec | undefined;
    invariant(
      target,
      `callout ${callout.id} bound to missing shape ${binding.toId}`,
    );
    return boundAnchorPage(target, binding.props);
  }
  return (callout.props as CalloutProps).target;
}

/** A polyline terminal bound by vertex id resolves to that exact point,
 *  immune to bounds changes from unrelated vertices. */
function boundVertexPage(
  target: ShapeRec,
  vertex: string | undefined,
): PagePoint | null {
  if (!vertex || target.type !== "line") return null;
  const points = target.props.points as
    | Record<string, { x: number; y: number }>
    | undefined;
  const point = points?.[vertex];
  if (!point) return null;
  const rotation = target.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  return {
    x: target.x + point.x * cos - point.y * sin,
    y: target.y + point.x * sin + point.y * cos,
  };
}

/** The span a dim currently measures, in page units. */
export function measuredSpanPage(
  records: SketchRecords,
  dim: ShapeRec,
): number {
  const a = dimEndpointPage(records, dim, "start");
  const b = dimEndpointPage(records, dim, "end");
  const props = dim.props as DimProps;
  if (props.direction === "x") return Math.abs(b.x - a.x);
  if (props.direction === "y") return Math.abs(b.y - a.y);
  return Math.hypot(b.x - a.x, b.y - a.y);
}
